// src/components/GlobalAlertProvider.tsx
import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROCESS } from '../lib/processConfig';
import AlertModal from './AlertModal';
import ConfirmModal from './ConfirmModal';

type DialogType = 'confirm' | 'alert';

type DialogContextType = {
  showTokenError(): Promise<void>;
  showAlert(message: string): Promise<void>;
  showAlert(title: string, message: string): Promise<void>;
  showConfirm(message: string): Promise<boolean>;
  showConfirm(title: string, message: string): Promise<boolean>;
  showConfirm(title: string, message: string, messageSub: string): Promise<boolean>;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialog must be used within DialogProvider');
  return context;
};

const GlobalAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DialogType>('alert');
  const [message, setMessage] = useState('');
  const [messageSub, setMessageSub] = useState('');
  const [title, setTitle] = useState('');
  const [resolver, setResolver] = useState<(value: any) => void>(() => () => {});

  const openDialog = (type: DialogType, title: string, message: string, messageSub?: string): Promise<any> => {
    setTitle(title);
    setMessage(message);
    setDialogType(type);
    setIsOpen(true);
    setMessageSub(messageSub || '');
    return new Promise((resolve) => setResolver(() => resolve));
  };

  const showConfirm = (arg1: string, arg2?: string, arg3?: string): Promise<boolean> => {
    if (arg2 !== undefined && arg3 !== undefined) {
      return openDialog('confirm', arg1, arg2, arg3);
    } else if (arg2 !== undefined) {
      return openDialog('confirm', arg1, arg2);
    } else {
      return openDialog('confirm', '확인', arg1);
    }
  };

  const showAlert = (arg1: string, arg2?: string): Promise<void> => {
    if (arg2 !== undefined) {
      return openDialog('alert', arg1, arg2);
    } else {
      return openDialog('alert', '', arg1);
    }
  };

  const showTokenError = () => {
    navigate('/home', { replace: true });

    return openDialog('alert', '경고', `사용 시간이 만료되었거나, 허가되지 않은 접근입니다... ${PROCESS._isProd ? '' : '(검증계)'}`);
  };

  const handleConfirm = () => {
    setIsOpen(false);
    resolver(true);
  };

  const handleCancel = () => {
    setIsOpen(false);
    resolver(false);
  };

  const handleAlertClose = () => {
    setIsOpen(false);
    resolver(true);
  };

  return (
    <DialogContext.Provider value={{ showAlert, showTokenError, showConfirm }}>
      {children}
      {dialogType === 'alert' ? (
        <AlertModal isOpen={isOpen} head={title} message={message} onClose={handleAlertClose} />
      ) : (
        <ConfirmModal
          isOpen={isOpen}
          head={title}
          message={message}
          messageSub={messageSub}
          onConfirm={handleConfirm}
          onClose={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
};

export default GlobalAlertProvider;
